import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography, Container, Paper, Button, Box, Chip, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControlLabel, Switch, CircularProgress, Divider,
  Menu, MenuItem, Select, FormControl, InputLabel, Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import {
  Add, Edit, Delete, AddCircleOutline, Lock,
  FormatBold, FormatItalic, FormatUnderlined, FormatListNumbered, FormatListBulleted,
  FormatAlignLeft, FormatAlignCenter, FormatAlignRight, FormatAlignJustify,
  HorizontalRule,
} from '@mui/icons-material';
import { insuranceDocumentTemplateService, insuranceDocumentTypeService } from '../../services/api';
import TopBar from '../../components/TopBar';

const AVAILABLE_FIELDS = [
  { key: '{{PatientName}}',          label: 'Patient Name' },
  { key: '{{PatientAge}}',           label: 'Patient Age' },
  { key: '{{PatientGender}}',        label: 'Patient Gender' },
  { key: '{{PatientAddress}}',       label: 'Patient Address' },
  { key: '{{PatientPhone}}',         label: 'Patient Mobile Number' },
  { key: '{{AadharNumber}}',         label: 'Aadhar Number' },
  { key: '{{PolicyNumber}}',         label: 'Policy Number' },
  { key: '{{MemberId}}',             label: 'Member ID' },
  { key: '{{InsurerName}}',          label: 'Insurer Name' },
  { key: '{{TPAName}}',              label: 'TPA Name' },
  { key: '{{ClaimType}}',            label: 'Claim Type' },
  { key: '{{AdmissionDate}}',        label: 'Admission Date' },
  { key: '{{DischargeDate}}',        label: 'Discharge Date' },
  { key: '{{AdmissionDiagnosis}}',   label: 'Admission Diagnosis' },
  { key: '{{Icd10Code}}',            label: 'ICD-10 Code' },
  { key: '{{WardType}}',             label: 'Ward / Room Type' },
  { key: '{{AdmittingDoctorName}}',  label: 'Admitting Doctor Name' },
  { key: '{{EstimatedAmount}}',      label: 'Estimated Amount' },
  { key: '{{ApprovedAmount}}',       label: 'Approved Amount' },
  { key: '{{SettledAmount}}',        label: 'Settled Amount' },
  { key: '{{ClaimNumber}}',          label: 'Claim Number' },
  { key: '{{TpaReferenceNumber}}',   label: 'TPA Reference Number' },
  { key: '{{HospitalName}}',         label: 'Hospital Name' },
  { key: '{{HospitalAddress}}',      label: 'Hospital Address' },
];

const FONT_SIZES = [
  { pt: 8, label: '8' }, { pt: 9, label: '9' }, { pt: 10, label: '10' },
  { pt: 11, label: '11' }, { pt: 12, label: '12' }, { pt: 13, label: '13' },
  { pt: 14, label: '14' }, { pt: 16, label: '16' }, { pt: 18, label: '18' },
  { pt: 20, label: '20' }, { pt: 24, label: '24' }, { pt: 28, label: '28' },
];

const emptyForm = { name: '', description: '', documentTypeId: '', templateBody: '', isActive: true };

const InsuranceDocumentTemplateManagement = () => {
  const navigate = useNavigate();

  const editorRef = useRef(null);
  const savedSelectionRef = useRef(null);

  const [templates, setTemplates] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editorKey, setEditorKey] = useState(0);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [fieldMenuAnchor, setFieldMenuAnchor] = useState(null);
  const [fontSizeValue, setFontSizeValue] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tplRes, typeRes] = await Promise.all([
        insuranceDocumentTemplateService.getAll(),
        insuranceDocumentTypeService.getAll(),
      ]);
      setTemplates(tplRes.data);
      setDocumentTypes(typeRes.data);
    } catch {
      alert('Failed to load templates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Populate the editor whenever the dialog opens or editorKey increments.
  useEffect(() => {
    if (!dialogOpen) return;
    const t = setTimeout(() => {
      if (!editorRef.current) return;
      const body = formData.templateBody || '';
      editorRef.current.innerHTML = body.trimStart().startsWith('<')
        ? body
        : body.replace(/\n/g, '<br>');
    }, 0);
    return () => clearTimeout(t);
  }, [editorKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const openAdd = () => {
    setEditingItem(null);
    setFormData(emptyForm);
    setEditorKey(k => k + 1);
    setDialogOpen(true);
  };

  const openEdit = async (row) => {
    try {
      const res = await insuranceDocumentTemplateService.getById(row.id);
      const body = res.data.templateBody || '';
      setEditingItem(row);
      setFormData({
        name: row.name,
        description: row.description || '',
        documentTypeId: row.documentTypeId || '',
        templateBody: body,
        isActive: row.isActive,
      });
      setEditorKey(k => k + 1);
      setDialogOpen(true);
    } catch {
      alert('Failed to load template');
    }
  };

  const handleSave = async () => {
    if (!formData.name.trim()) { alert('Name is required'); return; }
    setSaving(true);
    try {
      const payload = { ...formData, documentTypeId: formData.documentTypeId || null };
      if (editingItem) {
        await insuranceDocumentTemplateService.update(editingItem.id, payload);
      } else {
        await insuranceDocumentTemplateService.create(payload);
      }
      setDialogOpen(false);
      load();
    } catch (e) {
      alert(e?.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (row.isSystem) { alert('System templates cannot be deleted. Deactivate them instead.'); return; }
    if (!window.confirm(`Delete "${row.name}"?`)) return;
    try {
      await insuranceDocumentTemplateService.delete(row.id);
      load();
    } catch (e) {
      alert(e?.response?.data?.message || 'Failed to delete');
    }
  };

  // Sync editor HTML to formData after each keystroke / format change.
  const handleEditorInput = () => {
    const html = editorRef.current?.innerHTML || '';
    const isEmpty = html === '' || html === '<br>' || html === '<div><br></div>' || html === '<p><br></p>';
    setFormData(p => ({ ...p, templateBody: isEmpty ? '' : html }));
  };

  const applyFormat = (command) => {
    editorRef.current?.focus();
    document.execCommand(command, false, null);
    handleEditorInput();
  };

  const applyAlignment = (command) => {
    editorRef.current?.focus();
    document.execCommand(command, false, null);
    handleEditorInput();
  };

  const handleFontSizeMouseDown = () => {
    const sel = window.getSelection();
    if (sel?.rangeCount > 0 && editorRef.current?.contains(sel.anchorNode)) {
      savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const handleFontSizeChange = (e) => {
    const sizePt = e.target.value;
    if (!sizePt) return;
    editorRef.current?.focus();
    if (savedSelectionRef.current) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedSelectionRef.current.cloneRange());
      savedSelectionRef.current = null;
    }
    document.execCommand('fontSize', false, '7');
    editorRef.current?.querySelectorAll('font[size="7"]').forEach(el => {
      const span = document.createElement('span');
      span.style.fontSize = `${sizePt}pt`;
      span.innerHTML = el.innerHTML;
      el.parentNode.replaceChild(span, el);
    });
    handleEditorInput();
    setFontSizeValue('');
  };

  const insertHorizontalRule = () => {
    editorRef.current?.focus();
    document.execCommand('insertHTML', false,
      '<hr style="border:none;border-top:1px solid #bbb;margin:10px 0;" /><br>');
    handleEditorInput();
  };

  const handleInsertFieldOpen = (e) => {
    const sel = window.getSelection();
    if (sel?.rangeCount > 0) {
      savedSelectionRef.current = sel.getRangeAt(0).cloneRange();
    }
    setFieldMenuAnchor(e.currentTarget);
  };

  const insertField = (fieldKey) => {
    setFieldMenuAnchor(null);
    editorRef.current?.focus();
    if (savedSelectionRef.current) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedSelectionRef.current.cloneRange());
      savedSelectionRef.current = null;
    }
    document.execCommand('insertText', false, fieldKey);
    handleEditorInput();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopBar title="Insurance Document Templates" onBack={() => navigate('/dashboard')} />
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Paper elevation={2} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Create and manage templates for insurance documents (Pre-Authorization Request, Claim Form, etc.). Use <strong>{'{{FieldName}}'}</strong> placeholders to auto-fill patient, admission and insurance data when generating from the Document Packet.
          </Typography>
        </Paper>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mb: 2 }}>
          <Button variant="contained" startIcon={<Add />} onClick={openAdd}>Add Template</Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}><CircularProgress /></Box>
        ) : (
          <Paper elevation={3} sx={{ borderRadius: 2 }}>
            <Table>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                  <TableCell width={50}><strong>ID</strong></TableCell>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Linked Document Type</strong></TableCell>
                  <TableCell><strong>Description</strong></TableCell>
                  <TableCell width={120}><strong>Status</strong></TableCell>
                  <TableCell width={130}><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {templates.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                      No templates found.
                    </TableCell>
                  </TableRow>
                )}
                {templates.map(row => (
                  <TableRow key={row.id} hover>
                    <TableCell>{row.id}</TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography fontWeight={600}>{row.name}</Typography>
                        {row.isSystem && (
                          <Chip size="small" label="System" icon={<Lock sx={{ fontSize: '14px !important' }} />}
                            color="default" variant="outlined" sx={{ fontSize: '0.7rem', height: 20 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{row.documentTypeName || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">{row.description || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small"
                        label={row.isActive ? 'Active' : 'Inactive'}
                        color={row.isActive ? 'success' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={() => openEdit(row)}><Edit fontSize="small" /></IconButton>
                      </Tooltip>
                      <Tooltip title={row.isSystem ? 'System template — cannot delete' : 'Delete'}>
                        <span>
                          <IconButton size="small" color="error" onClick={() => handleDelete(row)} disabled={row.isSystem}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Container>

      {/* Add / Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingItem ? `Edit — ${editingItem.name}` : 'New Insurance Document Template'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <TextField
                label="Template Name *"
                value={formData.name}
                onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                fullWidth size="small"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={e => setFormData(p => ({ ...p, isActive: e.target.checked }))}
                    color="success"
                  />
                }
                label="Active"
                sx={{ whiteSpace: 'nowrap', ml: 1 }}
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Description"
                value={formData.description}
                onChange={e => setFormData(p => ({ ...p, description: e.target.value }))}
                fullWidth size="small" multiline rows={2}
              />
              <FormControl size="small" sx={{ minWidth: 260 }}>
                <InputLabel>Linked Document Type</InputLabel>
                <Select
                  label="Linked Document Type"
                  value={formData.documentTypeId}
                  onChange={e => setFormData(p => ({ ...p, documentTypeId: e.target.value }))}
                >
                  <MenuItem value="">— None —</MenuItem>
                  {documentTypes.map(t => (
                    <MenuItem key={t.id} value={t.id}>{t.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            <Divider />

            {/* Template Body editor */}
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.75 }}>Template Body</Typography>

              {/* ── Toolbar ── */}
              <Box sx={{
                display: 'flex', gap: 0.25, alignItems: 'center', flexWrap: 'wrap',
                px: 0.75, py: 0.5,
                border: '1px solid #ccc', borderBottom: 'none',
                borderRadius: '4px 4px 0 0',
                bgcolor: '#fafafa',
              }}>
                <Tooltip title="Bold (Ctrl+B)">
                  <IconButton size="small" onMouseDown={e => { e.preventDefault(); applyFormat('bold'); }}>
                    <FormatBold fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Italic (Ctrl+I)">
                  <IconButton size="small" onMouseDown={e => { e.preventDefault(); applyFormat('italic'); }}>
                    <FormatItalic fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Underline (Ctrl+U)">
                  <IconButton size="small" onMouseDown={e => { e.preventDefault(); applyFormat('underline'); }}>
                    <FormatUnderlined fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                <Tooltip title="Numbered List">
                  <IconButton size="small" onMouseDown={e => { e.preventDefault(); applyFormat('insertOrderedList'); }}>
                    <FormatListNumbered fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Bullet List">
                  <IconButton size="small" onMouseDown={e => { e.preventDefault(); applyFormat('insertUnorderedList'); }}>
                    <FormatListBulleted fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                <Tooltip title="Font Size (pt)">
                  <Select
                    size="small"
                    displayEmpty
                    value={fontSizeValue}
                    onMouseDown={handleFontSizeMouseDown}
                    onChange={handleFontSizeChange}
                    renderValue={() => <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>Size</Typography>}
                    sx={{ minWidth: 64, fontSize: '0.75rem', height: 30, '.MuiSelect-select': { py: '4px !important', px: '8px !important' } }}
                  >
                    {FONT_SIZES.map(s => (
                      <MenuItem key={s.pt} value={s.pt} dense>
                        <Typography sx={{ fontSize: `${s.pt}pt`, lineHeight: 1.4 }}>{s.label} pt</Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </Tooltip>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                <Tooltip title="Align Left">
                  <IconButton size="small" onMouseDown={e => { e.preventDefault(); applyAlignment('justifyLeft'); }}>
                    <FormatAlignLeft fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Align Center">
                  <IconButton size="small" onMouseDown={e => { e.preventDefault(); applyAlignment('justifyCenter'); }}>
                    <FormatAlignCenter fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Align Right">
                  <IconButton size="small" onMouseDown={e => { e.preventDefault(); applyAlignment('justifyRight'); }}>
                    <FormatAlignRight fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Justify">
                  <IconButton size="small" onMouseDown={e => { e.preventDefault(); applyAlignment('justifyFull'); }}>
                    <FormatAlignJustify fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                <Tooltip title="Insert Horizontal Line">
                  <IconButton size="small" onMouseDown={e => { e.preventDefault(); insertHorizontalRule(); }}>
                    <HorizontalRule fontSize="small" />
                  </IconButton>
                </Tooltip>

                <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddCircleOutline />}
                  onClick={handleInsertFieldOpen}
                >
                  Insert Field
                </Button>
              </Box>

              <Menu
                anchorEl={fieldMenuAnchor}
                open={Boolean(fieldMenuAnchor)}
                onClose={() => setFieldMenuAnchor(null)}
              >
                {AVAILABLE_FIELDS.map(f => (
                  <MenuItem key={f.key} onClick={() => insertField(f.key)} dense>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{f.label}</Typography>
                      <Typography variant="caption" color="text.secondary">{f.key}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Menu>

              <Box
                sx={{
                  border: '1px solid #ccc',
                  borderRadius: '0 0 4px 4px',
                  cursor: 'text',
                  '&:focus-within': {
                    borderColor: 'primary.main',
                    outline: '2px solid',
                    outlineColor: 'primary.main',
                    outlineOffset: '-1px',
                  },
                  '& ol, & ul': { paddingLeft: '1.5em', margin: '0.2em 0' },
                  '& p': { margin: '0 0 0.4em 0' },
                  '& hr': { border: 'none', borderTop: '1px solid #bbb', margin: '10px 0' },
                  '& table': { borderCollapse: 'collapse', width: '100%' },
                  '& td': { border: '1px solid #999', padding: '4px' },
                  overflow: 'hidden',
                }}
                onClick={() => editorRef.current?.focus()}
              >
                <div
                  ref={editorRef}
                  contentEditable
                  suppressContentEditableWarning
                  onInput={handleEditorInput}
                  style={{
                    outline: 'none',
                    minHeight: '220px',
                    maxHeight: '380px',
                    overflowY: 'auto',
                    padding: '12px 14px',
                    fontFamily: '"Times New Roman", serif',
                    fontSize: '13px',
                    lineHeight: 1.65,
                  }}
                />
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                Use toolbar buttons or <strong>Ctrl+B / Ctrl+I / Ctrl+U</strong> shortcuts. Select text then choose a font size. Use "Insert Field" to add dynamic placeholders like <strong>{'{{PatientName}}'}</strong> — auto-filled with real data when generating the document.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : (editingItem ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default InsuranceDocumentTemplateManagement;
